/**
 * previewNormalizer.js — NS-1 File 1 of 4 (extended for 16-section preview)
 *
 * Single source of truth for transforming either:
 *   - calculate-API result + wizard formData (Context A — Wizard Step 3 Preview)
 *   - saved quotation row + agency                 (Context B — QuotationDetail page)
 * into the canonical `previewData` shape consumed by `<QuotationPreview />`.
 *
 * Shape spec: /app/memory/NS1_PREVIEW_DATA_SHAPE.md
 *
 * Invariant: for the same logical quotation, both entry points must return
 * objects whose keys, types, and rendered values are identical (modulo:
 * meta.schemaVersion, header.quoteRef/createdAt — null in A; ui.readonly).
 *
 * No JSX. Pure module. No React imports.
 */

import {
  applyMarkupDistribution,
  transformCategoriesForPricing,
  vehicleUplift,
} from './quotationUtils';
import { formatCurrency } from '../components/QuotationDisplay/displayConfig';

// ============================================================================
//  Private helpers
// ============================================================================

const _safeParse = (val) => {
  if (val == null) return val;
  if (typeof val !== 'string') return val;
  try {
    return JSON.parse(val);
  } catch {
    return val;
  }
};

// FIX-4 (2026-05-10) — Empty arrays/objects are JS-truthy, so the idiom
// `qli?.foo || fallback || []` short-circuits to `[]` whenever the saved
// value exists but is empty. That's exactly how DMC stops landed empty
// and hotel dates showed "—" for an entire sprint. Use these helpers
// anywhere we cascade across multiple potential sources.
const _coalesceArr = (...candidates) =>
  candidates.find((c) => Array.isArray(c) && c.length > 0) || [];
const _coalesceObj = (...candidates) =>
  candidates.find((c) => c && typeof c === 'object' && !Array.isArray(c) && Object.keys(c).length > 0) || {};

const _toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const _packageType = (routeType) => {
  if (routeType === 'dmc_package') return 'dmc';
  if (routeType === 'manual_package') return 'manual';
  if (routeType === 'custom_package') return 'custom';
  return 'predefined';
};

// Meal-plan code → friendly label (e.g. CP → "Breakfast Only (CP)"). Used by
// Predefined hotel rows where the inner hotel dict doesn't carry `meal_plan`
// (it's carried at the quotation level as `selected_meal_plans[]`).
const _mealPlanLabel = (code) => {
  const c = String(code || '').toUpperCase();
  const map = {
    CP: 'Breakfast Only (CP)',
    EP: 'Room Only (EP)',
    MAP: 'Breakfast + Dinner (MAP)',
    AP: 'All Meals (AP)',
    AI: 'All Inclusive (AI)',
  };
  return map[c] || c || '';
};

// G2 helper — given an arrival date (YYYY-MM-DD) and ordered stops (with
// `nights`), compute each stop's check_in / check_out in ISO. Returns [] if
// inputs are unusable so callers can preserve legitimate falsy values.
const _computeStopDates = (arrivalDateStr, stops) => {
  if (!arrivalDateStr || !Array.isArray(stops) || stops.length === 0) return [];
  const base = new Date(arrivalDateStr);
  if (Number.isNaN(base.getTime())) return [];
  let cursor = new Date(base);
  return stops.map((s) => {
    const checkIn = new Date(cursor);
    const checkOut = new Date(cursor);
    checkOut.setDate(checkOut.getDate() + Number(s.nights || 0));
    cursor = checkOut;
    const iso = (d) => d.toISOString().slice(0, 10);
    return { checkIn: iso(checkIn), checkOut: iso(checkOut) };
  });
};


const _splitLines = (txt) =>
  typeof txt === 'string'
    ? txt.split('\n').map((s) => s.trim()).filter(Boolean)
    : Array.isArray(txt)
    ? txt.filter(Boolean)
    : [];

// ----- Stops + route -------------------------------------------------------

const _extractStops = ({ packageType, calculationResult, formData, quotation, qli }) => {
  if (formData) {
    if (packageType === 'manual') {
      const route = formData.manual_package_data?.route || [];
      return route.map((s, i) => ({
        placeId: s.place_id || null,
        placeName: s.place_name || `Stop ${i + 1}`,
        nights: _toNumber(s.nights, 1),
        order: i + 1,
      }));
    }
    if (packageType === 'custom') {
      const stops = formData.custom_package_data?.stops || [];
      return stops.map((s, i) => ({
        placeId: s.place_id || null,
        placeName: s.place_name || `Stop ${i + 1}`,
        nights: _toNumber(s.nights, 1),
        order: s.order ?? s.order_seq ?? i + 1,
      }));
    }
    if (packageType === 'dmc') {
      const hotels =
        calculationResult?.accommodation_options?.[0]?.hotels ||
        formData.dmc_content?.parsed?.accommodation_options?.[0]?.hotels ||
        [];
      return hotels.map((h, i) => ({
        placeId: null,
        placeName: h.city || h.destination || h.place_name || `Stop ${i + 1}`,
        nights: _toNumber(h.nights, 1),
        order: i + 1,
      }));
    }
    const cr = formData.custom_route || calculationResult?.custom_route || [];
    return cr.map((s, i) => ({
      placeId: s.place_id || null,
      placeName: s.place_name || `Stop ${i + 1}`,
      nights: _toNumber(s.nights, 1),
      order: s.order_seq ?? i + 1,
    }));
  }
  // FIX-4 (2026-05-10) — Use `_coalesceArr` to consistently treat empty
  // arrays as "missing" across all sources (qli, parsed-JSON column, formData
  // mirror). Replaces the previous ad-hoc `qli?.stops?.length ? qli.stops : null`
  // guard introduced for DISP-03 with a single uniform idiom.
  const stopsSrc = _coalesceArr(
    qli?.stops,
    packageType === 'manual'
      ? _safeParse(quotation?.manual_package_data)?.route
      : packageType === 'custom'
      ? _safeParse(quotation?.custom_package_data)?.stops
      : packageType === 'dmc'
      ? _safeParse(quotation?.dmc_content)?.parsed?.accommodation_options?.[0]?.hotels
      : null,
    packageType === 'manual'
      ? qli?.manual_package_data?.route
      : packageType === 'custom'
      ? qli?.custom_package_data?.stops
      : packageType === 'dmc'
      ? qli?.dmc_content?.parsed?.accommodation_options?.[0]?.hotels
      : null,
    _safeParse(quotation?.custom_route),
  );
  return (stopsSrc || []).map((s, i) => ({
    placeId: s.place_id || null,
    // DISP-02 (DMC) — DMC hotels carry the destination under `place`, not
    // `place_name`. Without this fallback the saved DMC route summary
    // collapses to "Stop 1 / Stop 2 …".
    placeName: s.place_name || s.place || s.city || s.destination || `Stop ${i + 1}`,
    nights: _toNumber(s.nights, 1),
    order: s.order ?? s.order_seq ?? i + 1,
  }));
};

const _buildRoute = (stops, arrivalCity, departureCity) => {
  const out = [];
  if (arrivalCity) out.push({ kind: 'arrival', city: arrivalCity, isCity: true });
  stops.forEach((s) =>
    out.push({
      kind: 'stop',
      placeId: s.placeId,
      placeName: s.placeName,
      nights: s.nights,
      order: s.order,
    })
  );
  if (departureCity) out.push({ kind: 'departure', city: departureCity, isCity: true });
  return out;
};

// ----- Hotels --------------------------------------------------------------

const _normaliseHotels = ({
  packageType,
  calculationResult,
  formData,
  quotation,
  qli,
  stops,
  primaryMealPlan,
  arrivalDate,
}) => {
  const groups = [];

  // G2 — precompute check_in/check_out for each stop position so we can
  // backfill when hotel payload omits them (common for Custom/Manual).
  const stopDates = _computeStopDates(arrivalDate, stops);

  if (packageType === 'predefined') {
    const recommended =
      calculationResult?.recommended_hotels ||
      _safeParse(quotation?.recommended_hotels) ||
      {};
    Object.entries(recommended).forEach(([cat, byPlace]) => {
      const hotels = [];
      Object.entries(byPlace || {}).forEach(([place, payload]) => {
        const arr = payload?.hotels || (payload?.hotel_name ? [payload] : []);
        const stopIdx = stops.findIndex(
          (s) => s.placeName?.toLowerCase() === place.toLowerCase()
        );
        const stopNights = stopIdx >= 0 ? stops[stopIdx].nights : 0;
        const fallbackDates = stopIdx >= 0 ? stopDates[stopIdx] : null;
        arr.forEach((h) =>
          hotels.push({
            stopIndex: stopIdx,
            destination: place,
            hotelName: h.hotel_name || 'TBC',
            roomName: h.room_name || '',
            // G3 — Predefined inner hotel dict has no `meal_plan`; fall back
            // to the quotation-level primary meal plan and render with a
            // friendly label (e.g. "Breakfast Only (CP)").
            mealPlan: _mealPlanLabel(h.meal_plan || primaryMealPlan || 'CP'),
            mealPlanCode: String(h.meal_plan || primaryMealPlan || 'CP').toUpperCase(),
            nights: _toNumber(h.nights, stopNights),
            ratePerNight: _toNumber(h.rate_per_night || h.lowest_rate, 0),
            roomCount: _toNumber(h.room_count ?? h.num_rooms ?? h.rooms_required, 0),
            gstSlab: _toNumber(h.room_gst_slab, 12),
            // G2 — prefer payload dates, fall back to computed stop dates.
            checkIn: h.check_in || fallbackDates?.checkIn || null,
            checkOut: h.check_out || fallbackDates?.checkOut || null,
          })
        );
      });
      if (hotels.length) groups.push({ key: cat, label: cat, hotels });
    });
    return { groupBy: 'category', groups };
  }

  if (packageType === 'custom') {
    let opts =
      calculationResult?.custom_package_options ||
      _safeParse(quotation?.custom_package_options) ||
      qli?.custom_package_options ||
      [];

    // 2026-09-27 — canonical source: qli.accommodation holds ONE row per
    // (option × stop), tagged with the option name in `category`. Before this,
    // the saved view fell through to the "fold every stop into Standard"
    // fallback and showed only the first option's hotels.
    if (!opts || opts.length === 0) {
      const accRows = Array.isArray(qli?.accommodation) ? qli.accommodation : [];
      if (accRows.length > 0) {
        const byOption = {};
        accRows.forEach((r) => {
          const key = r.category || r.room_category || 'Standard';
          if (!byOption[key]) byOption[key] = { option_name: key, hotels: [] };
          byOption[key].hotels.push({
            ...r,
            stop_index: r.stop_order ? Number(r.stop_order) - 1 : byOption[key].hotels.length,
            city: r.place_name,
            rate_per_night: r.room_rate_per_night ?? r.rate_per_night,
          });
        });
        opts = Object.values(byOption);
      }
    }

    // Next: the wizard's persisted per-option selections.
    if (!opts || opts.length === 0) {
      const cpdOpts =
        (formData?.custom_package_data ||
          _safeParse(quotation?.custom_package_data) ||
          qli?.custom_package_data ||
          {}).options || [];
      if (cpdOpts.length > 0) {
        opts = cpdOpts.map((o) => ({
          option_name: o.option_name || 'Standard',
          hotels: (o.stops || []).flatMap((s, si) =>
            (s.hotels || []).map((h) => ({
              ...h,
              stop_index: si,
              city: s.place_name,
              nights: h.nights || s.nights,
              check_in: h.check_in || s.check_in,
              check_out: h.check_out || s.check_out,
              rate_per_night: h.rate_per_night || h.lowest_rate,
            }))
          ),
        }));
      }
    }

    // Fallback: reconstruct option blocks from custom_package_data.stops[].options[]
    if (!opts || opts.length === 0) {
      const cpd =
        formData?.custom_package_data ||
        _safeParse(quotation?.custom_package_data) ||
        qli?.custom_package_data ||
        {};
      const stopsArr = cpd.stops || [];
      if (stopsArr.length > 0) {
        const optionsByName = {};
        let hasAnyOption = false;
        stopsArr.forEach((stop, si) => {
          (stop.options || []).forEach((o) => {
            hasAnyOption = true;
            const key = o.option_name || 'Standard';
            if (!optionsByName[key]) optionsByName[key] = { option_name: key, hotels: [] };
            optionsByName[key].hotels.push({
              ...o,
              stop_index: si,
              city: stop.place_name,
              nights: stop.nights,
            });
          });
        });
        // Second fallback — OP-2 save path stores hotels directly under each
        // stop (no per-stop `options[]` layer). Fold them all into a single
        // "Standard" option so the hotel table can render.
        if (!hasAnyOption) {
          const singleOption = { option_name: 'Standard', hotels: [] };
          stopsArr.forEach((stop, si) => {
            (stop.hotels || []).forEach((h) => {
              singleOption.hotels.push({
                ...h,
                stop_index: si,
                city: h.city || h.destination || stop.place_name,
                nights: h.nights || stop.nights,
                check_in: h.check_in || stop.check_in,
                check_out: h.check_out || stop.check_out,
              });
            });
          });
          if (singleOption.hotels.length) optionsByName.Standard = singleOption;
        }
        opts = Object.values(optionsByName);
      }
    }

    // Third fallback — Context B: recommended_hotels is keyed by actual
    // category (e.g. "Luxury"), each value maps place → {hotel_name, ...}.
    // Convert to the option-shape expected by the hotel renderer.
    if ((!opts || opts.length === 0) && quotation?.recommended_hotels) {
      const rh = _safeParse(quotation.recommended_hotels) || {};
      const stopsForDates = stops || [];
      Object.entries(rh).forEach(([catKey, byPlace]) => {
        if (!byPlace || typeof byPlace !== 'object') return;
        const hotels = [];
        Object.entries(byPlace).forEach(([placeName, info]) => {
          if (!info || typeof info !== 'object') return;
          const hotelArr = info.hotels || [info];
          hotelArr.forEach((h) => {
            const si = stopsForDates.findIndex(
              (s) => (s.placeName || '').toLowerCase() === placeName.toLowerCase()
            );
            hotels.push({
              ...h,
              stop_index: si >= 0 ? si : hotels.length,
              city: placeName,
              nights: h.nights || info.nights || 1,
              check_in: h.check_in || info.check_in || null,
              check_out: h.check_out || info.check_out || null,
            });
          });
        });
        if (hotels.length) opts.push({ option_name: catKey, hotels });
      });
    }

    opts.forEach((opt) => {
      const hotels = (opt.hotels || []).map((h, i) => {
        const stopIdx = h.stop_index ?? i;
        const stopNights = stops[stopIdx]?.nights ?? _toNumber(h.nights, 1);
        const fallbackDates = stopIdx >= 0 ? stopDates[stopIdx] : null;
        return {
          stopIndex: stopIdx,
          destination: stops[stopIdx]?.placeName || h.city || h.destination || '',
          hotelName: h.hotel_name || 'TBC',
          // Room-display contract (2026-02): room_name only, no cascade.
          roomName: h.room_name || '',
          mealPlan: _mealPlanLabel(h.meal_plan || primaryMealPlan || 'CP'),
          mealPlanCode: String(h.meal_plan || primaryMealPlan || 'CP').toUpperCase(),
          nights: _toNumber(h.nights, stopNights),
          ratePerNight: _toNumber(h.rate_per_night, 0),
          roomCount: _toNumber(h.room_count ?? h.num_rooms ?? h.rooms_required, 0),
          gstSlab: _toNumber(h.room_gst_slab, 12),
          // G2
          checkIn: h.check_in || fallbackDates?.checkIn || null,
          checkOut: h.check_out || fallbackDates?.checkOut || null,
        };
      });
      groups.push({ key: opt.option_name || 'Standard', label: opt.option_name || 'Standard', hotels });
    });
    return { groupBy: 'option', groups };
  }

  if (packageType === 'manual') {
    const mpd =
      formData?.manual_package_data ||
      _safeParse(quotation?.manual_package_data) ||
      {};
    const opts = mpd.options || [];
    const route = mpd.route || [];
    opts.forEach((opt) => {
      const hotels = (opt.accommodation || []).map((acc, i) => {
        const fallbackDates = stopDates[i] || null;
        return {
          stopIndex: i,
          destination:
            acc.place_name || acc.place || acc.destination || route[i]?.place_name || `Stop ${i + 1}`,
          hotelName: acc.hotel_name || 'TBC',
          roomName: acc.room_name || '',
          mealPlan: _mealPlanLabel(acc.meal_plan || primaryMealPlan || 'CP'),
          mealPlanCode: String(acc.meal_plan || primaryMealPlan || 'CP').toUpperCase(),
          nights: _toNumber(acc.nights, 1),
          ratePerNight: _toNumber(acc.rate_per_night, 0),
          roomCount: _toNumber(acc.room_count || acc.num_rooms || acc.rooms_required, 0),
          gstSlab: _toNumber(acc.room_gst_slab, 12),
          // G2
          checkIn: acc.check_in || fallbackDates?.checkIn || null,
          checkOut: acc.check_out || fallbackDates?.checkOut || null,
        };
      });
      groups.push({ key: opt.option_name || 'Standard', label: opt.option_name || 'Standard', hotels });
    });
    return { groupBy: 'option', groups };
  }

  const dmc =
    calculationResult?.accommodation_options ||
    _safeParse(quotation?.dmc_content)?.parsed?.accommodation_options ||
    formData?.dmc_content?.parsed?.accommodation_options ||
    [];
  dmc.forEach((opt) => {
    const hotels = (opt.hotels || []).map((h, i) => {
      const fallbackDates = stopDates[i] || null;
      return {
        stopIndex: i,
        destination: h.place || h.city || h.destination || '',
        hotelName: h.hotel || h.hotel_name || 'TBC',
        roomName: h.room_name || '',
        mealPlan: _mealPlanLabel(h.meal_plan || primaryMealPlan || 'CP'),
        mealPlanCode: String(h.meal_plan || primaryMealPlan || 'CP').toUpperCase(),
        nights: _toNumber(h.nights, 1),
        ratePerNight: _toNumber(h.rate_per_night, 0),
        roomCount: _toNumber(h.room_count ?? h.num_rooms ?? h.rooms_required, 0),
        gstSlab: _toNumber(h.room_gst_slab, 0),
        // G2
        checkIn: h.check_in || fallbackDates?.checkIn || null,
        checkOut: h.check_out || fallbackDates?.checkOut || null,
      };
    });
    groups.push({ key: opt.option_name || opt.category || 'Standard', label: opt.option_name || opt.category || 'Standard', hotels });
  });
  return { groupBy: 'option', groups };
};

// ----- Sketch itinerary ---------------------------------------------------

const _normaliseItinerary = ({ packageType, calculationResult, formData, qli, stops, arrivalCity, departureCity, dmcRoute }) => {
  if (packageType === 'dmc') {
    const itin =
      calculationResult?.itinerary ||
      qli?.itinerary ||
      formData?.dmc_content?.parsed?.itinerary ||
      [];
    // D1 FIX — derive overnight_at by walking `parsed.route[]` nights when
    // the itinerary entry lacks an explicit overnight_at. Last day is
    // "Departure" (empty overnight → rendered as "-" by the component).
    const route = dmcRoute || [];
    const nightSeq = [];
    route.forEach((s) => {
      const n = Number(s?.nights || 0);
      const place = s?.place_name || s?.place || '';
      for (let k = 0; k < n; k++) nightSeq.push(place);
    });
    return itin.map((d, i) => {
      let overnight = d.overnight_at || d.overnight_location || '';
      if (!overnight && i < itin.length - 1) {
        overnight = nightSeq[i] || '';
      }
      return {
        day: _toNumber(d.day, i + 1),
        title: d.title || `Day ${i + 1}`,
        highlights: d.description || d.highlights || '',
        overnightAt: overnight,
        isArrival: i === 0,
        isDeparture: i === itin.length - 1,
      };
    });
  }

  const days = [];
  let dayNum = 1;
  if (stops.length > 0) {
    const first = stops[0];
    days.push({
      day: dayNum++,
      title: arrivalCity ? `Arrival ${arrivalCity} → ${first.placeName}` : `Arrival → ${first.placeName}`,
      highlights: '',
      overnightAt: first.placeName,
      isArrival: true,
    });
    for (let si = 0; si < stops.length; si++) {
      const stop = stops[si];
      const next = stops[si + 1];
      const sightDays = si === 0 ? Math.max(0, stop.nights - 1) : stop.nights - 1;
      for (let s = 0; s < sightDays; s++) {
        days.push({
          day: dayNum++,
          title: `${stop.placeName} Sightseeing`,
          highlights: '',
          overnightAt: stop.placeName,
        });
      }
      if (next) {
        days.push({
          day: dayNum++,
          title: `${stop.placeName} → ${next.placeName}`,
          highlights: '',
          overnightAt: next.placeName,
        });
      }
    }
    days.push({
      day: dayNum++,
      title: departureCity ? `Departure from ${departureCity}` : 'Departure',
      highlights: '',
      overnightAt: '',
      isDeparture: true,
    });
  }
  return days;
};

// ----- Day-wise detailed itinerary (§12) ----------------------------------

const _normaliseDetailedItinerary = ({ packageType, calculationResult, formData, qli, quotation, stops }) => {
  // Context B: prefer saved data first.
  const saved =
    qli?.detailed_itinerary ||
    _safeParse(quotation?.detailed_itinerary) ||
    [];

  // BUG-02 helper — walk stops × nights to compute the per-day overnight
  // city when the saved itinerary entries don't carry overnight_at. Last day
  // = empty (Departure → component renders "-").
  const _computeOvernightSeq = (stopsArr, totalDays) => {
    const seq = [];
    if (!Array.isArray(stopsArr) || stopsArr.length === 0) return seq;
    // Day 1 = arrival → first stop
    seq.push(stopsArr[0].placeName || stopsArr[0].place_name || '');
    for (let si = 0; si < stopsArr.length; si++) {
      const stop = stopsArr[si];
      const next = stopsArr[si + 1];
      const sightDays = si === 0 ? Math.max(0, (stop.nights || 1) - 1) : (stop.nights || 1) - 1;
      for (let s = 0; s < sightDays; s++) seq.push(stop.placeName || stop.place_name || '');
      if (next) seq.push(next.placeName || next.place_name || '');
    }
    seq.push(''); // departure day
    return seq;
  };

  if (saved.length) {
    const overnightSeq = _computeOvernightSeq(stops || [], saved.length);
    return saved.map((d, i) => ({
      // BUG-02 — backend `fetch_detailed_itinerary` emits `day_number /
      // day_title / itinerary_text`; canonical/manual paths emit `day /
      // title / description`. Read both shapes so §12 populates for all
      // package types.
      day: _toNumber(d.day_number ?? d.day, i + 1),
      title: d.day_title || d.title || `Day ${i + 1}`,
      date: d.date || null,
      description: d.itinerary_text || d.description || '',
      highlights: d.highlights || '',
      meals: d.meals || '',
      optionalActivities: d.optional_activities || '',
      // BUG-02 — overnight_at falls back to per-day stops walk (Manual logic
      // applied to Predefined / Custom). Previously empty → display defaulted
      // to "Departure" for every row.
      overnightAt: d.overnight_at || d.overnight_location || overnightSeq[i] || '',
    }));
  }

  // DMC opportunistic — works in both contexts because parsed itinerary is always present
  if (packageType === 'dmc') {
    const src =
      calculationResult?.itinerary ||
      formData?.dmc_content?.parsed?.itinerary ||
      _safeParse(quotation?.dmc_content)?.parsed?.itinerary ||
      qli?.dmc_content?.parsed?.itinerary ||
      qli?.itinerary ||
      [];
    if (src.length && src.some((d) => d.description || d.highlights)) {
      return src.map((d, i) => ({
        day: _toNumber(d.day, i + 1),
        title: d.title || `Day ${i + 1}`,
        date: null,
        description: d.description || '',
        highlights: d.highlights || '',
        meals: d.meals || '',
        optionalActivities: d.optional_activities || '',
        overnightAt: d.overnight_at || d.overnight_location || '',
      }));
    }
  }

  return [];
};

// ----- Vehicle -------------------------------------------------------------

// Percent markup is the only form applied per-vehicle (fixed-amount markup is
// spread across the whole package) — mirrors QuotationFormatter.get_vehicle_data.
const _vehicleMarkupFactor = (src) =>
  (src?.markup_type || 'percent') === 'percent'
    ? 1 + _toNumber(src?.markup_pct, 0) / 100
    : 1;

// Vehicle-only subtotal with markup. Route-wise (PER_ROUTE) rates are the TOTAL
// transport cost for the package route — never × days; city-to-city (PER_DAY)
// rates are per-day. The engine already resolved this into `transfer_cost`, so
// prefer it and only fall back to re-deriving from the rate.
const _vehicleSubtotalWithMarkup = (o, fallbackDays, markupFactor) => {
  const count = _toNumber(o?.vehicle_count, 1) || 1;
  const engineCost = _toNumber(o?.transfer_cost, 0);
  if (engineCost > 0) return Math.round(engineCost * markupFactor * 100) / 100;
  const rate = _toNumber(o?.rate_amount ?? o?.rate_per_day, 0);
  const days = _toNumber(o?.days, fallbackDays) || 0;
  const isPerRoute = !!o?.is_per_route || o?.contract_type === 'PER_ROUTE';
  const base = isPerRoute ? rate * count : rate * days * count;
  return Math.round(base * markupFactor * 100) / 100;
};


const _normaliseVehicle = ({ packageType, calculationResult, formData, qli, quotation }) => {
  if (packageType === 'manual') {
    // M2 FIX — Manual vehicle data lives in `options[i].package_transfer`
    // (vehicle_name, rate_per_vehicle, num_vehicles, gst_pct) with
    // `options[i].transfer_total` as the pre-computed total. The legacy
    // `o.transfers` path produced TBC/₹0 and is no longer populated.
    const mpd = formData?.manual_package_data || _safeParse(quotation?.manual_package_data) || {};
    const opts = mpd.options || [];
    // BUG-05c — Manual `transfer_days` was rendering as 0 in §7. Compute
    // from the route nights when `legacy.days` is missing (canonical path
    // since legacy.days is no longer populated by the wizard).
    const route = mpd.route || [];
    const totalRouteNights = route.reduce((s, r) => s + Number(r?.nights || 0), 0);
    const totalDaysFallback = totalRouteNights > 0 ? totalRouteNights + 1 : 0;
    const manualVehicles = opts.map((o) => {
      const pt = o.package_transfer || {};
      const legacy = o.transfers || {};
      const vehicleName =
        pt.vehicle_name || legacy.vehicle_type || mpd.vehicle_type || 'TBC';
      const ratePerVehicle = _toNumber(pt.rate_per_vehicle, 0) || _toNumber(legacy.rate, 0);
      const numVehicles = _toNumber(pt.num_vehicles, 1) || _toNumber(legacy.no_of_vehicles, 1);
      const days = _toNumber(legacy.days, totalDaysFallback);
      const total = _toNumber(
        o.transfer_total,
        ratePerVehicle * numVehicles * days,
      );
      return {
        optionName: o.option_name || 'Standard',
        vehicleType: vehicleName,
        ratePerDay: ratePerVehicle, // route-level per-vehicle rate
        ratePerDayDisplay: ratePerVehicle > 0 ? `₹${ratePerVehicle.toLocaleString('en-IN')}` : 'Route Based',
        days,
        noOfVehicles: numVehicles,
        total,
      };
    });
    return {
      hasOptions: false,
      showUpgradeTable: false,
      selected:
        manualVehicles[0] || { vehicleType: 'TBC', days: 0, ratePerDay: 0, total: 0, isBase: true },
      options: [],
      manualVehicles,
    };
  }

  if (packageType === 'dmc') {
    const dmcVeh =
      calculationResult?.vehicle ||
      _safeParse(quotation?.dmc_content)?.parsed?.vehicle ||
      formData?.dmc_content?.parsed?.vehicle ||
      'As per DMC';

    // BUG-14 — DMC may carry MULTIPLE vehicle options inside
    // dmc_content.parsed.vehicle_options[]. The previous code returned only
    // the first/string vehicle. Surface the full list so all options render.
    const dmcVehOptions =
      calculationResult?.vehicle_options ||
      _safeParse(quotation?.dmc_content)?.parsed?.vehicle_options ||
      formData?.dmc_content?.parsed?.vehicle_options ||
      [];
    const options = (Array.isArray(dmcVehOptions) ? dmcVehOptions : []).map((o, i) => ({
      vehicleType: o.vehicle_type || o.vehicle_label || o.label || `Option ${i + 1}`,
      days: _toNumber(o.days, 0),
      ratePerDay: _toNumber(o.rate_per_day || o.rate, 0),
      total: _toNumber(o.total || o.total_quoted, 0),
      uplift: _toNumber(o.uplift || o.uplift_vs_base, 0),
      isBase: i === 0,
    }));

    return {
      hasOptions: options.length > 0,
      showUpgradeTable: options.length > 1,
      selected: options[0] || {
        vehicleType: typeof dmcVeh === 'string' ? dmcVeh : (dmcVeh?.vehicle_type || 'As per DMC'),
        days: 0,
        ratePerDay: 0,
        total: 0,
        isBase: true,
      },
      options,
      manualVehicles: null,
    };
  }

  if (packageType === 'custom') {
    // C2 FIX — Custom vehicle lives in qli.selected_vehicle, written by
    // OP-2 save path. Carry vehicle_type, rate_per_day, days, total.
    // BUG-05b / BUG-14 — also surface vehicle_upgrade_options[] when
    // calculator emitted multiple vehicle alternatives. Previously truncated
    // to a single selected entry.
    const sv =
      calculationResult?.selected_vehicle ||
      qli?.selected_vehicle ||
      _safeParse(quotation?.quote_line_items)?.selected_vehicle ||
      {};
    const vt =
      sv.vehicle_type ||
      sv.vehicle_label ||
      calculationResult?.vehicle_type ||
      quotation?.vehicle_type_selected ||
      formData?.custom_package_data?.vehicle_type ||
      'Vehicle';
    const days = _toNumber(sv.days, _toNumber(calculationResult?.total_days || quotation?.transfer_days, 0));
    const rate = _toNumber(sv.rate_per_day, 0);
    const total = _toNumber(sv.total, rate * days);

    const customUpgradeRaw =
      calculationResult?.vehicle_upgrade_options ||
      qli?.vehicle_upgrade_options ||
      _safeParse(quotation?.vehicle_upgrade_options) ||
      [];
    const cMarkupFactor = _vehicleMarkupFactor(formData || quotation || qli || {});
    const cBaseType =
      qli?.base_vehicle_type || calculationResult?.base_vehicle_type || vt;
    const cUpgradeList = Array.isArray(customUpgradeRaw) ? customUpgradeRaw : [];
    const cBaseOpt =
      cUpgradeList.find((o) => (o.vehicle_type || o.vehicleType) === cBaseType) || cUpgradeList[0];
    const options = cUpgradeList.map((o) => {
      const oVt = o.vehicle_type || o.vehicleType || o.vehicle_label || '';
      const oDays = _toNumber(o.days, days);
      const oRate = _toNumber(o.rate_amount ?? o.rate_per_day, 0);
      const oTotal = _vehicleSubtotalWithMarkup(o, days, cMarkupFactor);
      const oIsBase = oVt === cBaseType;
      return {
        vehicleType: oVt,
        days: oDays,
        ratePerDay: oRate,
        noOfVehicles: _toNumber(o.vehicle_count, 1) || 1,
        total: oTotal,
        uplift: oIsBase ? 0 : vehicleUplift(o, cBaseOpt, (x) => _vehicleSubtotalWithMarkup(x, days, cMarkupFactor)),
        isBase: oIsBase,
      };
    });

    return {
      hasOptions: options.length > 0,
      showUpgradeTable: options.length > 1,
      selected: options.find((o) => o.isBase) || {
        vehicleType: vt,
        days,
        ratePerDay: rate,
        noOfVehicles: _toNumber(sv.vehicle_count ?? qli?.vehicle_count ?? quotation?.vehicle_count, 1) || 1,
        total,
        uplift: 0,
        isBase: true,
      },
      options,
      manualVehicles: null,
    };
  }

  const veh = calculationResult || qli || {};
  // Base vehicle = the one the operator assigned (persisted as
  // qli.base_vehicle_type). Mirrors QuotationFormatter.get_vehicle_data so the
  // saved view, preview and PDF all anchor uplift to the same vehicle.
  const baseVehicleType =
    veh.base_vehicle_type ||
    qli?.base_vehicle_type ||
    veh.vehicle_type ||
    quotation?.vehicle_type ||
    quotation?.vehicle_type_selected ||
    formData?.vehicle_type ||
    '';
  const baseDays = _toNumber(
    veh.transfer_days || quotation?.transfer_days || formData?.transfer_days,
    0
  );
  const baseRate = _toNumber(veh.vehicle_rate_per_day || veh.rate_per_day, 0);
  const markupFactor = _vehicleMarkupFactor(formData || quotation || qli || {});

  // BUG-05a — backend `pricing_engine_sql.py::calculate_pricing` emits
  // {vehicle_type, vehicle_label, vehicle_count, transfer_cost, total_quoted,
  //  uplift_vs_base, contract_type}. `total_quoted` is a category-blended
  //  package total (Bug 2.3), so totals/uplift are recomputed here as
  //  rate × days × count × (1 + markup%) — identical to the PDF formatter.
  const upgradeOpts = veh.vehicle_upgrade_options || _safeParse(quotation?.vehicle_upgrade_options) || [];
  const _optTotal = (o) => _vehicleSubtotalWithMarkup(o, baseDays, markupFactor);
  const _effBaseType =
    (upgradeOpts.some((o) => (o.vehicle_type || o.vehicleType) === baseVehicleType)
      ? baseVehicleType
      : (upgradeOpts.find((o) => o.is_base) || {}).vehicle_type) || baseVehicleType;
  const _baseOpt =
    upgradeOpts.find((o) => (o.vehicle_type || o.vehicleType) === _effBaseType) || upgradeOpts[0];
  const options = upgradeOpts.map((o) => {
    const oVt = o.vehicle_type || o.vehicleType || o.vehicle_label || '';
    const oDays = _toNumber(o.days, baseDays);
    const oTransferCost = _toNumber(o.transfer_cost, 0);
    const oCount = _toNumber(o.vehicle_count, 1);
    const oRatePerDay = _toNumber(
      o.rate_amount ?? o.rate_per_day,
      // Derive from transfer_cost / count / days when calculator only emits
      // canonical fields.
      oCount > 0 && oDays > 0 ? oTransferCost / oCount / oDays : 0,
    );
    const oTotal = _optTotal(o);
    const oIsBase = oVt === _effBaseType;
    return {
      vehicleType: oVt,
      days: oDays,
      ratePerDay: oRatePerDay,
      noOfVehicles: oCount || 1,
      total: oTotal,
      uplift: oIsBase ? 0 : vehicleUplift(o, _baseOpt, _optTotal),
      isBase: oIsBase,
    };
  });

  const selected =
    options.find((o) => o.isBase) ||
    options[0] || {
      vehicleType: baseVehicleType,
      days: baseDays,
      ratePerDay: baseRate,
      noOfVehicles: _toNumber(veh.vehicle_count ?? qli?.vehicle_count ?? quotation?.vehicle_count, 1) || 1,
      total: _toNumber(veh.vehicle_total || veh.total, baseRate * baseDays * markupFactor),
      uplift: 0,
      isBase: true,
    };

  return {
    hasOptions: options.length > 0,
    showUpgradeTable: options.length > 1,
    selected,
    options,
    manualVehicles: null,
  };
};

// ----- Vehicle summary pill for §3 (manual=null per spec) ------------------
const _buildVehicleSummary = (packageType, vehicle) => {
  if (packageType === 'manual') return null;
  const t = vehicle?.selected?.vehicleType;
  if (!t || t === 'TBC') return packageType === 'dmc' ? 'As per DMC' : null;
  const days = _toNumber(vehicle?.selected?.days, 0);
  return days > 0 ? `${t} · ${days} days` : t;
};

// ----- Misc costs ----------------------------------------------------------

const _normaliseMiscCosts = ({ calculationResult, formData, qli, quotation }) => {
  const pickFirstNonEmpty = (...arrs) => {
    for (const a of arrs) {
      if (Array.isArray(a) && a.length > 0) return a;
      if (a && !Array.isArray(a) && Array.isArray(a.items) && a.items.length > 0) return a.items;
    }
    return [];
  };
  const arr = pickFirstNonEmpty(
    calculationResult?.misc_costs,
    qli?.misc_costs,
    _safeParse(quotation?.misc_costs),
    formData?.misc_costs,
    // Batch-2 §9: Manual saves can stash misc costs under
    // manual_package_data.misc_costs. Same for Custom under custom_package_data.
    formData?.manual_package_data?.misc_costs,
    formData?.custom_package_data?.misc_costs,
    _safeParse(quotation?.manual_package_data)?.misc_costs,
    _safeParse(quotation?.custom_package_data)?.misc_costs,
  );
  const included = [];
  const optional = [];
  // BUG-03 — saved misc_cost rows store {remarks, cost_type, amount,
  // calculated_amount, gst_percentage, gst_amount, total_with_gst,
  // is_optional}. The downstream <MiscCostsTable> reads
  // {remarks, costType, rate, quantity, amount, gstPercentage, gstAmount,
  // total, isOptional}. Map both shapes so Description / Type / Rate / Qty
  // / GST / Total all populate.
  const _costTypeLabel = (t) => {
    const m = {
      fixed: 'Fixed',
      per_person: 'Per Person',
      per_room: 'Per Room',
      per_night: 'Per Night',
    };
    return m[t] || (t ? String(t) : 'Fixed');
  };
  arr.forEach((c) => {
    const rate = _toNumber(c.amount, 0);
    const calculated = _toNumber(c.calculated_amount, rate);
    // Quantity inferred from calculated_amount / rate (×3 pax → qty 3).
    const qty = rate > 0 ? Math.round(calculated / rate) : 1;
    const gstPct = _toNumber(c.gst_percentage ?? c.gst_slab, 0);
    const gstAmt = _toNumber(c.gst_amount, 0);
    const total = _toNumber(c.total_with_gst, calculated + gstAmt);
    const item = {
      // Canonical MiscCostsTable field names
      remarks: c.remarks || c.description || c.name || '',
      costType: _costTypeLabel(c.cost_type),
      rate: formatCurrency(rate),
      quantity: qty || 1,
      amount: formatCurrency(calculated),
      gstPercentage: gstPct,
      gstAmount: formatCurrency(gstAmt),
      total: formatCurrency(total),
      isOptional: !!c.is_optional,
      // Legacy shape preserved so any other consumer still works.
      description: c.remarks || c.description || c.name || '',
      city: c.city || '',
      amountRaw: calculated,
      gstSlab: gstPct,
      totalWithGst: total,
    };
    if (c.is_optional) optional.push(item);
    else included.push(item);
  });
  return {
    hasItems: included.length + optional.length > 0,
    included,
    optional,
    totals: {
      included: included.reduce((sum, x) => sum + (x.totalWithGst || x.amount), 0),
      optional: optional.reduce((sum, x) => sum + (x.totalWithGst || x.amount), 0),
    },
  };
};

// ----- P2P transfers -------------------------------------------------------

const _normaliseP2P = ({ calculationResult, formData, qli, quotation }) => {
  const pickFirstNonEmpty = (...arrs) => {
    for (const a of arrs) {
      if (Array.isArray(a) && a.length > 0) return a;
    }
    return [];
  };
  const arr = pickFirstNonEmpty(
    calculationResult?.p2p_transfers,
    qli?.p2p_transfers,
    _safeParse(quotation?.p2p_transfers),
    formData?.manual_package_data?.p2p_transfers,
    formData?.p2p_transfers,
    // Batch-3 §8: saved Manual quotes store P2P inside the
    // manual_package_data JSON column. Saved Custom quotes likewise under
    // custom_package_data. formData is null in the saved-view path, so the
    // fallbacks above all miss — pull from quotation.* explicitly.
    _safeParse(quotation?.manual_package_data)?.p2p_transfers,
    _safeParse(quotation?.custom_package_data)?.p2p_transfers,
  );
  const included = [];
  const optional = [];
  const rows = arr.map((t) => {
    const isIncluded = !t.is_optional;
    const row = {
      fromPlace: t.from_place || '',
      toPlace: t.to_place || '',
      description: t.description || '',
      vehicleType: t.vehicle_type || '',
      variantName: t.variant_name || '',
      pricingType: t.pricing_type || 'FIXED',
      rate: _toNumber(t.rate, 0),
      quantity: _toNumber(t.quantity, 1),
      calculatedAmount: _toNumber(t.calculated_amount || t.total_amount || t.total_cost || t.cost, 0),
      gstAmount: _toNumber(t.gst_amount, 0),
      gstSlab: _toNumber(t.gst_slab || t.gst_percentage, 0),
      isIncluded,
      typeLabel: isIncluded ? 'Included' : 'Optional',
    };
    if (isIncluded) included.push(row);
    else optional.push(row);
    return row;
  });
  return {
    hasTransfers: rows.length > 0,
    included,
    optional,
    rows,
    totals: {
      included: included.reduce((sum, x) => sum + x.calculatedAmount, 0),
      optional: optional.reduce((sum, x) => sum + x.calculatedAmount, 0),
    },
  };
};

// ----- Pricing block -------------------------------------------------------

const _normalisePricing = ({ packageType, formData, quotation, qli, calculationResult, format, totalPax, payingPax, miscIncludedTotal }) => {
  let categoryPricing =
    calculationResult?.category_pricing ||
    qli?.category_pricing ||
    {};

  // Fallback for Custom-package legacy schema: no category_pricing but flat pricing at qli root
  const isEmpty = !categoryPricing || Object.keys(categoryPricing).length === 0;
  if (isEmpty) {
    const src = qli || quotation || {};
    const netQP = _toNumber(src.net_quoted_price, 0);
    const grossCost = _toNumber(src.gross_cost, 0);
    if (netQP > 0 || grossCost > 0) {
      categoryPricing = {
        Standard: {
          hotel_cost: _toNumber(src.hotel_cost, 0),
          transfer_cost: _toNumber(src.transfer_cost, 0),
          p2p_cost: _toNumber(src.p2p_cost, 0),
          misc_cost: _toNumber(src.misc_cost, 0),
          gross_cost: grossCost,
          markup_amount: _toNumber(src.markup_amount, 0),
          discount_amount: _toNumber(src.discount_amount, 0),
          net_quoted_price: netQP,
          per_person_price: _toNumber(src.per_person_price, 0),
          gst_amount: _toNumber(src.gst_amount, 0),
          gst_percentage: _toNumber(src.gst_percentage, 0),
          gst_included: src.gst_included !== false,
          total_with_gst: _toNumber(src.total_with_gst, netQP),
          adult_price: _toNumber(src.adult_price, 0),
          adult_extra_bed_price: _toNumber(src.adult_extra_bed_price, 0),
          child_with_bed_price: _toNumber(src.child_with_bed_price, 0),
          child_no_bed_price: _toNumber(src.child_no_bed_price, 0),
          infant_price: _toNumber(src.infant_price, 0),
        },
      };
    }
  }

  const rows = transformCategoriesForPricing(categoryPricing, { packageType });

  // Batch-1 §10: build `mealPlanTables` from qli.meal_plan_pricing when the
  // quotation was calculated for 2+ meal plans. Each entry renders as one
  // <MealPlanPricingTable>. Single-meal-plan and Custom/Manual/DMC single
  // option quotes fall through to the legacy single-table render.
  const mealPlanOrder = ['EP', 'CP', 'MAP', 'AP', 'AI'];
  const mealPlanLabelMap = {
    EP: 'Room Only (EP)',
    CP: 'Breakfast Only (CP)',
    MAP: 'Breakfast + Dinner (MAP)',
    AP: 'All Meals (AP)',
    AI: 'All Inclusive (AI)',
  };
  const mealPlanPricingRaw = (qli && qli.meal_plan_pricing) || (calculationResult && calculationResult.meal_plan_pricing) || {};
  // Bug 2.2: skipped (category × meal_plan) rows produced by the backend when
  // selected hotels lack a rate for the requested meal_plan. Rendered as a
  // footnote below the affected pricing table.
  const missingMealPlans = (
    (qli && qli.missing_meal_plans) ||
    (calculationResult && calculationResult.missing_meal_plans) ||
    []
  );
  const mealPlanKeys = Object.keys(mealPlanPricingRaw)
    .filter((k) => mealPlanPricingRaw[k] && (mealPlanPricingRaw[k].category_pricing || {}))
    .sort((a, b) => {
      const ia = mealPlanOrder.indexOf(a);
      const ib = mealPlanOrder.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  const mealPlanTables = mealPlanKeys.length > 1
    ? mealPlanKeys.map((mp) => {
        const cpForMp = mealPlanPricingRaw[mp]?.category_pricing || {};
        const missingForMp = missingMealPlans.filter((m) => m && m.meal_plan === mp);
        const mpTotalRows = Object.entries(cpForMp).map(([cat, p]) => {
          const dist = applyMarkupDistribution({
            hotel_cost: p.hotel_cost,
            transfer_cost: p.transfer_cost,
            p2p_cost: p.p2p_cost,
            markup_amount: p.markup_amount,
          });
          return {
            key: cat,
            label: cat,
            grossCost: _toNumber(p.gross_cost, 0),
            hotelTotal: _toNumber(p.hotel_cost, 0),
            transferTotal: _toNumber(p.transfer_cost, 0),
            p2pTotal: _toNumber(p.p2p_cost, 0),
            markupAmount: _toNumber(p.markup_amount, 0),
            discountAmount: _toNumber(p.discount_amount, 0),
            netQuotedPrice: _toNumber(p.net_quoted_price, 0),
            gstAmount: _toNumber(p.gst_amount, 0),
            gstPercentage: _toNumber(p.gst_percentage, 0),
            gstIncluded: !!p.gst_included,
            totalWithGst: _toNumber(p.total_with_gst, p.net_quoted_price),
            perPersonPrice: _toNumber(p.per_person_price, 0),
            displayHotel: dist.displayHotel,
            displayTransfer: dist.displayTransfer,
          };
        });
        const mpPersonWise = Object.entries(cpForMp).map(([cat, p]) => ({
          key: cat,
          label: cat,
          adultPrice: _toNumber(p.adult_price, 0),
          adultExtraBedPrice: _toNumber(p.adult_extra_bed_price, 0),
          childWithBedPrice: _toNumber(p.child_with_bed_price, 0),
          childNoBedPrice: _toNumber(p.child_no_bed_price, 0),
          infantPrice: _toNumber(p.infant_price, 0),
          perPersonPrice: _toNumber(p.per_person_price, 0),
          totalWithGst: _toNumber(p.total_with_gst, p.net_quoted_price),
          netQuotedPrice: _toNumber(p.net_quoted_price, 0),
          gstAmount: _toNumber(p.gst_amount, 0),
        }));
        return {
          mealPlan: mp,
          mealPlanLabel: mealPlanLabelMap[mp] || mp,
          totalRows: mpTotalRows,
          personWiseRows: mpPersonWise,
          missingCategories: missingForMp,  // Bug 2.2
        };
      })
    : [];

  const totalRows = Object.entries(categoryPricing).map(([key, p]) => {
    const dist = applyMarkupDistribution({
      hotel_cost: p.hotel_cost,
      transfer_cost: p.transfer_cost,
      p2p_cost: p.p2p_cost,
      markup_amount: p.markup_amount,
    });
    return {
      key,
      label: key,
      grossCost: _toNumber(p.gross_cost, 0),
      hotelTotal: _toNumber(p.hotel_cost, 0),
      transferTotal: _toNumber(p.transfer_cost, 0),
      miscTotal: _toNumber(p.misc_cost, 0),
      markupAmount: _toNumber(p.markup_amount, 0),
      discountAmount: _toNumber(p.discount_amount, 0),
      netQuotedPrice: _toNumber(p.net_quoted_price, 0),
      gstAmount: _toNumber(p.gst_amount, 0),
      gstPercentage: _toNumber(p.gst_percentage, 0),
      gstIncluded: !!p.gst_included,
      totalWithGst: _toNumber(p.total_with_gst, p.net_quoted_price),
      perPersonPrice: _toNumber(p.per_person_price, 0),
      displayHotel: dist.displayHotel,
      displayTransfer: dist.displayTransfer,
    };
  });

  const personWiseRows = Object.entries(categoryPricing).map(([key, p]) => ({
    key,
    label: key,
    adultPrice: _toNumber(p.adult_price, 0),
    adultExtraBedPrice: _toNumber(p.adult_extra_bed_price, 0),
    childWithBedPrice: _toNumber(p.child_with_bed_price, 0),
    childNoBedPrice: _toNumber(p.child_no_bed_price, 0),
    infantPrice: _toNumber(p.infant_price, 0),
    perPersonPrice: _toNumber(p.per_person_price, 0),
    totalWithGst: _toNumber(p.total_with_gst, p.net_quoted_price),
    netQuotedPrice: _toNumber(p.net_quoted_price, 0),
    gstAmount: _toNumber(p.gst_amount, 0),
  }));

  const grandTotal = totalRows.length === 1
    ? (totalRows[0].totalWithGst || 0) + (miscIncludedTotal || 0)
    // Multi-option (cat×meal) quotations: the customer picks ONE option and
    // the payment schedule / grand total must reflect that option, not a sum
    // across all options. We default to the PRIMARY (first) option's total.
    // Downstream §14 schedule uses this base; multi-option display can still
    // show per-row totals from `totalRows[]`. The previous `sum(...)` caused
    // §14 instalment amounts to compute against an inflated base.
    : (totalRows[0]?.totalWithGst || 0) + (miscIncludedTotal || 0);
  // Canonical I2/Q5: per-head figure divides by PAYING pax (adults + children
  // with bed) only — children-without-bed and infants don't dilute the rate.
  const _payingPax = payingPax > 0 ? payingPax : totalPax;
  const grandTotalPerPerson = _payingPax > 0 ? Math.round(grandTotal / _payingPax) : 0;

  const src = formData || quotation || {};
  // "Show Cost Breakup in Quote" toggle — gates the Hotels/Transfers split
  // columns in the pricing tables. Defaults ON when the flag is absent.
  const showBreakup = (
    formData?.show_breakup ?? quotation?.show_breakup ?? qli?.show_breakup
  ) !== false;
  return {
    format,
    showBreakup,
    totalRows,
    personWiseRows,
    mealPlanTables,        // Batch-1 §10: per-meal-plan pricing tables (multi-MP quotes)
    missingMealPlans,      // Bug 2.2: [{category, meal_plan, places, hotels, reason}]
    rows,
    grandTotal,
    grandTotalPerPerson,
    totalPax,
    markupSummary: {
      type: src.markup_type || 'percent',
      value: _toNumber(src.markup_pct ?? src.markup_value, 0),
      amount: totalRows.reduce((s, r) => s + r.markupAmount, 0),
    },
    discountSummary: {
      type: src.discount_type || 'percent',
      value: _toNumber(src.discount_pct ?? src.discount_value, 0),
      amount: totalRows.reduce((s, r) => s + r.discountAmount, 0),
      showSeparately: !!src.show_discount_separately,  // Bug 2: gate Discount column
    },
    gstSummary: {
      percentage: _toNumber(src.gst_percentage, 0),
      applyOn: src.gst_apply_on || 'total',
      included: src.gst_included !== false,
    },
  };
};

// ----- Payment policy (§14) ------------------------------------------------

/**
 * Accepts the snapshot embedded by the backend on `quotation.payment_policy`
 * (Context B) or `formData.payment_policy_snapshot` (Context A).
 * Computes instalment amounts against the live/saved grandTotal.
 * Balance instalments absorb the rounding residual so the schedule always
 * sums back to grandTotal.
 */
const _normalisePaymentPolicy = ({ policy, grandTotal, tourStartDate }) => {
  if (!policy || !Array.isArray(policy.instalments) || policy.instalments.length === 0) {
    return { hasPolicy: false, policyName: null, schedule: [], total: 0 };
  }

  const sorted = [...policy.instalments].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  let running = 0;
  const schedule = sorted.map((i, idx) => {
    const type = i.amount_type;
    const value = _toNumber(i.amount_value, 0);
    let amount = 0;
    if (type === 'percentage') {
      amount = Math.round((grandTotal * value) / 100);
    } else if (type === 'fixed') {
      amount = Math.min(value, Math.max(0, grandTotal - running));
    } else {
      // balance → absorbs residual
      amount = Math.max(0, grandTotal - running);
    }
    running += amount;
    let dueDate = null;
    if (tourStartDate && i.days_before_arrival != null) {
      try {
        const d = new Date(tourStartDate);
        d.setDate(d.getDate() - _toNumber(i.days_before_arrival, 0));
        dueDate = d.toISOString().split('T')[0];
      } catch {
        dueDate = null;
      }
    }
    return {
      order: i.order ?? idx + 1,
      label: i.label || (type === 'balance' ? 'Balance' : `Instalment ${idx + 1}`),
      type,
      value,
      daysBeforeArrival: _toNumber(i.days_before_arrival, 0),
      dueDate,
      amount,
    };
  });

  return {
    hasPolicy: true,
    policyName: policy.policy_name || 'Standard',
    isDefault: !!policy.is_default,
    schedule,
    total: running,
  };
};

// ----- Terms & conditions (§15) + Inclusions/Exclusions (§13) --------------

const FALLBACK_PAYMENT_TERMS = [
  'No payment terms configured — please add your agency payment terms in Settings → Agency → Payment Terms.',
];

const FALLBACK_CANCELLATION = [
  'No cancellation policy configured — please add your agency cancellation policy in Settings → Agency → Cancellation Policy.',
];

const FALLBACK_GENERAL = [
  'No terms configured — please add your agency terms in Settings → Agency → Quote Terms.',
];

const _normaliseTerms = ({ formData, quotation, agencyMasters }) => {
  const m = agencyMasters || {};

  const qIncl = typeof quotation?.inclusions === 'string' ? quotation.inclusions : null;
  const qExcl = typeof quotation?.exclusions === 'string' ? quotation.exclusions : null;
  const qPay = typeof quotation?.payment_terms === 'string' ? quotation.payment_terms : null;
  const qCan = typeof quotation?.cancellation_policy === 'string' ? quotation.cancellation_policy : null;
  const qTerms = typeof quotation?.terms_conditions === 'string' ? quotation.terms_conditions : null;

  const fIncl = typeof formData?.inclusions === 'string' ? formData.inclusions : null;
  const fExcl = typeof formData?.exclusions === 'string' ? formData.exclusions : null;
  const fPay = typeof formData?.payment_terms === 'string' ? formData.payment_terms : null;
  const fCan = typeof formData?.cancellation_policy === 'string' ? formData.cancellation_policy : null;
  const fTerms = typeof formData?.terms_conditions === 'string' ? formData.terms_conditions : null;

  const inclusionsList =
    _splitLines(qIncl ?? fIncl).length > 0
      ? _splitLines(qIncl ?? fIncl)
      : m.inclusions || [];
  const exclusionsList =
    _splitLines(qExcl ?? fExcl).length > 0
      ? _splitLines(qExcl ?? fExcl)
      : m.exclusions || [];

  const payFromOverride = _splitLines(qPay ?? fPay);
  const payFromMaster = m.payment_terms || [];
  const paymentTermsList = payFromOverride.length > 0 ? payFromOverride : payFromMaster;

  const canFromOverride = _splitLines(qCan ?? fCan);
  const canFromMaster = m.cancellation_policy || [];
  const cancellationList = canFromOverride.length > 0 ? canFromOverride : canFromMaster;

  const genFromOverride = _splitLines(qTerms ?? fTerms);
  const genFromMaster = m.terms_conditions || [];
  const generalTermsList = genFromOverride.length > 0 ? genFromOverride : genFromMaster;

  const usingFallback =
    paymentTermsList.length === 0 &&
    cancellationList.length === 0 &&
    generalTermsList.length === 0;

  return {
    hasTerms: true, // always rendered per spec
    usingFallback,
    inclusionsList: inclusionsList.length > 0 ? inclusionsList : [],
    exclusionsList: exclusionsList.length > 0 ? exclusionsList : [],
    paymentTermsList: paymentTermsList.length > 0 ? paymentTermsList : (usingFallback ? FALLBACK_PAYMENT_TERMS : []),
    cancellationList: cancellationList.length > 0 ? cancellationList : (usingFallback ? FALLBACK_CANCELLATION : []),
    generalTermsList: generalTermsList.length > 0 ? generalTermsList : (usingFallback ? FALLBACK_GENERAL : []),
  };
};

// ----- Bank details (§16) --------------------------------------------------

const _normaliseBankDetails = (agency) => {
  if (!agency) return { hasBankDetails: false };
  const showBank = agency.show_bank_in_quotation !== false;
  const showUpi = agency.show_upi_in_quotation !== false;
  const has = !!(showBank && agency.bank_account_number);
  if (!has && !(showUpi && agency.upi_id)) return { hasBankDetails: false };
  return {
    hasBankDetails: has,
    bankName: agency.bank_name || '',
    accountName: agency.bank_account_name || '',
    accountNumber: agency.bank_account_number || '',
    ifscCode: agency.bank_ifsc || '',
    branch: agency.bank_branch || '',
    upi: showUpi && agency.upi_id
      ? { upiId: agency.upi_id, qrUrl: agency.upi_qr_url || null }
      : null,
  };
};

// ============================================================================
//  Public entry points
// ============================================================================

export function normalizePreviewData(calculationResult, formData, agency = null) {
  const cr = calculationResult || {};
  const fd = formData || {};
  const packageType = _packageType(fd.route_type);

  const stops = _extractStops({ packageType, calculationResult: cr, formData: fd });
  // M6 FIX — Manual / Custom pull arrival/departure from their package_data
  // wrappers when the top-level form fields are unset at Step 3 render.
  const _mpd = _safeParse(fd.manual_package_data) || {};
  const _cpd = _safeParse(fd.custom_package_data) || {};
  const _effArrivalCity =
    fd.arrival_city || _cpd.arrival_city || _mpd.arrival_city || '';
  const _effDepartureCity =
    fd.departure_city ||
    _cpd.departure_city ||
    _mpd.departure_city ||
    _effArrivalCity;
  const route = _buildRoute(stops, _effArrivalCity, _effDepartureCity);

  const adults = _toNumber(fd.no_adults, 0);
  const cwb = _toNumber(fd.no_children_with_bed, 0);
  const cnb = _toNumber(fd.no_children_no_bed, 0);
  const infants = _toNumber(fd.no_infants, 0);
  const totalPax = adults + cwb + cnb;
  const totalNights = stops.reduce((s, st) => s + (st.nights || 0), 0);

  const miscCosts = _normaliseMiscCosts({ calculationResult: cr, formData: fd });
  // Ticket #285275 (#9/#10): saved quotes store 'per_person', the form uses 'person_wise' - accept both.
  const format = ['person_wise', 'per_person'].includes(fd.quotation_format) ? 'person_wise' : 'total';
  const vehicle = _normaliseVehicle({ packageType, calculationResult: cr, formData: fd });

  const pricing = _normalisePricing({
    packageType,
    formData: fd,
    calculationResult: cr,
    format,
    totalPax,
    payingPax: adults + cwb,
    miscIncludedTotal: miscCosts.totals.included,
  });

  const paymentPolicy = _normalisePaymentPolicy({
    policy: fd.payment_policy_snapshot || null,
    grandTotal: pricing.grandTotal,
    tourStartDate: fd.tour_start_date || fd.arrival_date,
  });

  // G4 / G5 / D2 — meta flags that gate header-level UI.
  // For Predefined: use selected_categories × selected_meal_plans matrix.
  // For Custom/DMC/Manual: pricing is per-option; count actual category_pricing
  // entries (one rendered table per entry).
  const _cpKeys = Object.keys(cr?.category_pricing || {});
  const _cpEntries = _cpKeys.map((k) => cr.category_pricing[k] || {});
  const _distinctMealsInCp = new Set(
    _cpEntries.map((e) => e.meal_plan).filter((m) => m && m !== 'None')
  );
  const numCategories = packageType === 'predefined'
    ? (Array.isArray(fd.selected_categories) ? fd.selected_categories.length : 0)
    : _cpKeys.length;
  const numMealPlans = packageType === 'predefined'
    ? (Array.isArray(fd.selected_meal_plans) ? fd.selected_meal_plans.length : 0)
    : _distinctMealsInCp.size;
  // G4 — show Grand Total Summary only for single-category AND ≤ 1 meal plan
  const showGrandTotalSummary = numCategories <= 1 && numMealPlans <= 1;
  // G5 — whether to show a separate GST column in pricing tables / summary
  const showGstColumn =
    fd.show_gst_separately === true || cr?.show_gst_separately === true;
  // D2 — flag to tell QuotationPreview.jsx to pick the DMC inclusions list
  // from termsAndConditions.inclusionsList and NOT also render the generic
  // Inclusions section (otherwise two "Inclusions" sections appear).
  const usesDmcInclusions = packageType === 'dmc';

  return {
    meta: {
      packageType,
      schemaVersion: '2.0',
      format,
      currency: cr.currency_info?.source_currency || 'INR',
      currencyRate: cr.currency_info?.conversion_rate || null,
      showGrandTotalSummary,
      showGstColumn,
      usesDmcInclusions,
    },
    header: {
      quoteRef: null,
      quoteId: null,
      createdAt: null,
      validityDate: fd.validity_date || null,
      version: 1,
      status: 'draft',
    },
    guest: {
      name: fd.guest_name || '',
      email: fd.guest_email || '',
      phone: fd.guest_phone || '',
      countryCode: fd.guest_country_code || '+91',
    },
    trip: {
      arrivalCity: fd.arrival_city || '',
      departureCity: fd.departure_city || '',
      arrivalDate: fd.arrival_date || fd.tour_start_date || null,
      tourStartDate: fd.tour_start_date || fd.arrival_date || null,
      totalNights,
      totalDays: totalNights + 1,
    },
    pax: {
      adults,
      childrenWithBed: cwb,
      childrenNoBed: cnb,
      infants,
      total: totalPax,
      rooms: fd.room_config || { single_rooms: 0, double_rooms: 0, triple_rooms: 0 },
      summary: _summarisePax({ adults, cwb, cnb, infants, rooms: fd.room_config }),
      vehicleSummary: _buildVehicleSummary(packageType, vehicle),
    },
    route,
    hotels: _normaliseHotels({
      packageType,
      calculationResult: cr,
      formData: fd,
      stops,
      primaryMealPlan: (Array.isArray(fd.selected_meal_plans) && fd.selected_meal_plans[0]) || fd.meal_plan || 'CP',
      arrivalDate: fd.arrival_date || fd.tour_start_date,
    }),
    itinerary: _normaliseItinerary({
      packageType,
      calculationResult: cr,
      formData: fd,
      stops,
      arrivalCity: fd.arrival_city,
      departureCity: fd.departure_city,
      dmcRoute: fd.dmc_content?.parsed?.route || cr?.route || [],
    }),
    detailedItinerary: _normaliseDetailedItinerary({
      packageType,
      calculationResult: cr,
      formData: fd,
      stops,
    }),
    vehicle,
    miscCosts,
    p2pTransfers: _normaliseP2P({ calculationResult: cr, formData: fd }),
    pricing,
    paymentPolicy,
    termsAndConditions: _normaliseTerms({ formData: fd, agencyMasters: fd.agency_masters }),
    inclusions: fd.inclusions || cr.inclusions || [],
    exclusions: fd.exclusions || cr.exclusions || [],
    bankDetails: { hasBankDetails: false }, // hidden in Context A per spec
    agency: agency || {},
    ui: {
      readonly: false,
      callbacks: null,
      hasChanges: false,
    },
  };
}

export function normalizeSavedQuotation(quotation, agency = null) {
  const q = quotation || {};
  const qli = _safeParse(q.quote_line_items) || {};
  const packageType = _packageType(q.route_type);
  const agencyMasters = q.agency_masters || {};
  // Batch-1 §16: when no explicit agency is passed but the GET endpoint
  // embedded `q.agency` (bank + branding), use it so §16 Bank Details renders.
  const _agency = agency || q.agency || null;

  const stops = _extractStops({ packageType, quotation: q, qli });
  // M6 FIX parity — fall back to saved package_data wrappers when the
  // top-level columns lack the values.
  const _mpdB = _safeParse(q.manual_package_data) || {};
  const _cpdB = _safeParse(q.custom_package_data) || {};
  const _arrB = q.arrival_city || _cpdB.arrival_city || _mpdB.arrival_city || '';
  const _depB = q.departure_city || _cpdB.departure_city || _mpdB.departure_city || _arrB;
  const route = _buildRoute(stops, _arrB, _depB);

  const adults = _toNumber(q.no_adults, 0);
  const cwb = _toNumber(q.no_children_with_bed, 0);
  const cnb = _toNumber(q.no_children_no_bed, 0);
  const infants = _toNumber(q.no_infants, 0);
  const totalPax = adults + cwb + cnb;
  const roomConfig = _safeParse(q.room_config) || {
    single_rooms: _toNumber(q.single_rooms, 0),
    double_rooms: _toNumber(q.double_rooms, 0),
    triple_rooms: _toNumber(q.triple_rooms, 0),
    adult_extra_beds: _toNumber(q.extra_adults, 0),
  };
  const totalNights = _toNumber(qli.total_nights, stops.reduce((s, st) => s + (st.nights || 0), 0));

  const miscCosts = _normaliseMiscCosts({ qli, quotation: q });

  const rawFormat = qli.quotation_format || q.quotation_format || 'total';
  // Ticket #285275 (#9/#10): saved quotes store 'per_person', the form uses 'person_wise' - accept both.
  const format = ['person_wise', 'personwise', 'per_person'].includes(rawFormat) ? 'person_wise' : 'total';

  const vehicle = _normaliseVehicle({ packageType, quotation: q, qli, calculationResult: qli });
  // #289720: show only the base + the uplift vehicles the agent ticked.
  // Quotes saved before this field existed carry no list and stay unfiltered.
  const _pickedVehicles = _safeParse(qli?.selected_vehicles) || _safeParse(q.selected_vehicles);
  if (Array.isArray(_pickedVehicles) && _pickedVehicles.length > 0 && Array.isArray(vehicle?.options)) {
    vehicle.options = vehicle.options.filter(
      (o) => o.isBase || _pickedVehicles.includes(o.vehicleType)
    );
    vehicle.showUpgradeTable = vehicle.options.length > 1;
  }

  const pricing = _normalisePricing({
    packageType,
    quotation: q,
    qli,
    format,
    totalPax,
    payingPax: adults + cwb,
    miscIncludedTotal: miscCosts.totals.included,
  });

  const paymentPolicy = _normalisePaymentPolicy({
    policy: q.payment_policy || qli.payment_policy_snapshot || null,
    grandTotal: pricing.grandTotal,
    tourStartDate: q.tour_start_date || q.arrival_date,
  });

  // G4 / G5 / D2 — parity with Context A.
  const _cpKeysB = Object.keys(qli.category_pricing || {});
  const _cpEntriesB = _cpKeysB.map((k) => qli.category_pricing[k] || {});
  const _distinctMealsInCpB = new Set(
    _cpEntriesB.map((e) => e.meal_plan).filter((m) => m && m !== 'None')
  );
  const selectedCats = _safeParse(q.selected_categories) || [];
  const selectedMeals = _safeParse(q.selected_meal_plans) || [];
  const numCategoriesB = packageType === 'predefined'
    ? (Array.isArray(selectedCats) ? selectedCats.length : 0)
    : _cpKeysB.length;
  const numMealPlansB = packageType === 'predefined'
    ? (Array.isArray(selectedMeals) ? selectedMeals.length : 0)
    : _distinctMealsInCpB.size;
  const showGrandTotalSummary = numCategoriesB <= 1 && numMealPlansB <= 1;
  const showGstColumn = q.show_gst_separately === true || qli?.show_gst_separately === true;
  const usesDmcInclusions = packageType === 'dmc';

  return {
    meta: {
      packageType,
      schemaVersion: qli.schema_version || '1.0',
      format,
      currency: qli.currency_info?.source_currency || 'INR',
      currencyRate: qli.currency_info?.conversion_rate || null,
      showGrandTotalSummary,
      showGstColumn,
      usesDmcInclusions,
    },
    header: {
      quoteRef: q.quote_reference || null,
      quoteId: q.quotation_id || null,
      createdAt: q.created_at || null,
      validityDate: q.validity_date || null,
      version: _toNumber(q.version, 1),
      status: q.status || 'draft',
    },
    guest: {
      name: q.guest_name || '',
      email: q.guest_email || '',
      phone: q.guest_phone || '',
      countryCode: q.guest_country_code || '+91',
    },
    trip: {
      arrivalCity: q.arrival_city || '',
      departureCity: q.departure_city || '',
      arrivalDate: q.arrival_date || q.tour_start_date || null,
      tourStartDate: q.tour_start_date || q.arrival_date || null,
      totalNights,
      totalDays: _toNumber(qli.total_days, totalNights + 1),
    },
    pax: {
      adults,
      childrenWithBed: cwb,
      childrenNoBed: cnb,
      infants,
      total: totalPax,
      rooms: roomConfig,
      summary: _summarisePax({ adults, cwb, cnb, infants, rooms: roomConfig }),
      vehicleSummary: _buildVehicleSummary(packageType, vehicle),
    },
    route,
    hotels: _normaliseHotels({
      packageType,
      quotation: q,
      qli,
      stops,
      primaryMealPlan: (Array.isArray(q.selected_meal_plans) && q.selected_meal_plans[0]) || q.meal_plan || 'CP',
      arrivalDate: q.arrival_date || q.tour_start_date,
    }),
    itinerary: _normaliseItinerary({
      packageType,
      qli,
      stops,
      arrivalCity: q.arrival_city,
      departureCity: q.departure_city,
      dmcRoute:
        _safeParse(q.dmc_content)?.parsed?.route ||
        qli?.dmc_content?.parsed?.route ||
        [],
    }),
    detailedItinerary: _normaliseDetailedItinerary({ packageType, qli, quotation: q, stops }),
    vehicle,
    miscCosts,
    p2pTransfers: _normaliseP2P({ qli, quotation: q }),
    pricing,
    paymentPolicy,
    termsAndConditions: _normaliseTerms({ quotation: q, agencyMasters }),
    // §13 convenience arrays — component reads termsAndConditions.inclusionsList / exclusionsList
    inclusions:
      packageType === 'dmc'
        ? _safeParse(q.dmc_content)?.parsed?.inclusions || qli.inclusions || []
        : _safeParse(q.inclusions) || qli.inclusions || [],
    exclusions:
      packageType === 'dmc'
        ? _safeParse(q.dmc_content)?.parsed?.exclusions || qli.exclusions || []
        : _safeParse(q.exclusions) || qli.exclusions || [],
    bankDetails: _normaliseBankDetails(_agency),
    agency: _agency || {},
    ui: {
      readonly: true,
      callbacks: null,
      hasChanges: false,
    },
  };
}

// ----- internal -----

const _summarisePax = ({ adults, cwb, cnb, infants, rooms }) => {
  const parts = [];
  if (adults) parts.push(`${adults} Adult${adults !== 1 ? 's' : ''}`);
  if (cwb) parts.push(`${cwb}C(bed)`);
  if (cnb) parts.push(`${cnb}C(no bed)`);
  if (infants) parts.push(`${infants}I`);
  const r = rooms || {};
  const roomParts = [];
  if (r.single_rooms) roomParts.push(`${r.single_rooms} Single`);
  if (r.double_rooms) roomParts.push(`${r.double_rooms} Double`);
  if (r.triple_rooms) roomParts.push(`${r.triple_rooms} Triple`);
  return [parts.join(' · '), roomParts.join(', ')].filter(Boolean).join(' • ');
};
